import {get} from './helpers';

import {labelMaker} from './helpers';
import {DEFAULT_TRANSLATIONS, PRICE_SUMMARY, RATE_TYPES} from './constants';

export const getSafeTranslations = (
    translations = {},
    defaultTranslations = DEFAULT_TRANSLATIONS
) => {
    return Object.entries(translations).reduce((result, [key, value]) => {
        result[key] =
            (typeof value === 'string' && value) || defaultTranslations[key];
        return result;
    }, defaultTranslations);
};

export const getLocation = (hit) => {
    if (hit.listing && hit.listing.address) {
        const city = get(hit, 'listing.address.city', '');
        const stateProv = get(hit, 'listing.address.stateProvince');
        const country = get(hit, 'listing.address.country');
        return (
            city +
            (stateProv ? `, ${stateProv}` : '') +
            (country ? `, ${country}` : '')
        );
    }

    return get(hit, 'listing.baseLocation.name.full');
};

export const getAccommodations = (hit, translations) => {
    let result = '';
    const bdCount = get(hit, 'listing.bedrooms');
    if (bdCount) {
        result += labelMaker(translations.bedroomsAbbreviation, {
            value: bdCount,
        });
    }
    const bathroomCount = get(hit, 'listing.bathrooms.full');
    if (bathroomCount) {
        if (result !== '') {
            result += ' · ';
        }
        result += labelMaker(translations.bathroomsAbbreviation, {
            value: bathroomCount,
        });
    }
    const sleepsCount = get(hit, 'listing.sleeps');
    if (sleepsCount) {
        if (result !== '') {
            result += ' · ';
        }
        result += labelMaker(translations.sleeps, {
            value: sleepsCount,
        });
    }
    return result;
};

export const getLegacyAccommodations = (hit, translations) => {
    let result = '';
    const sleepsCount = get(hit, 'listing.sleeps');
    if (sleepsCount) {
        if (result !== '') {
            result += ' · ';
        }
        result += labelMaker(translations.sleeps, {
            value: sleepsCount,
        });
    }
    const bdCount = get(hit, 'listing.bedrooms');
    if (bdCount && result !== '') {
        if (result !== '') {
            result += ' · ';
        }
        result += labelMaker(translations.bedroomsAbbreviation, {
            value: bdCount,
        });
    }

    return result;
};

export const getRate = (rateType) => (hit, translation) => {
    const defaultValue = '';
    const rateValue = get(hit, `listing.${rateType}.localized`, defaultValue);

    if (!rateValue) {
        return defaultValue;
    }

    const rateValueNums = rateValue.match(/\d/g);
    const rateIsZero = rateValueNums.length === 1 && rateValueNums[0] === '0';

    if (rateIsZero) {
        return defaultValue;
    }

    const htmlTranslation = translation.replace(
        '{price}',
        '<strong>{price}</strong>'
    );

    return labelMaker(htmlTranslation, {
        price: rateValue,
    });
};

export const getAverageRate = getRate(RATE_TYPES.AVERAGE);
export const getTotalRate = getRate(RATE_TYPES.TOTAL);

const getPriceSummary = (summaryType) => (hit) => {
    const formattedAmount = get(hit, `listing.${summaryType}.formattedAmount`);
    const pricePeriodDescription = get(
        hit,
        `listing.${summaryType}.pricePeriodDescription`
    );
    const priceAvailable = pricePeriodDescription && formattedAmount;
    const startsWithSlash = priceAvailable && pricePeriodDescription[0] === '/';

    return (
        priceAvailable &&
        `<strong>${formattedAmount}</strong>${
            startsWithSlash
                ? pricePeriodDescription
                : ` ${pricePeriodDescription}`
        }`
    );
};

export const getAveragePriceSummary = getPriceSummary(PRICE_SUMMARY.AVERAGE);

export const getImageUrl = ({listing = {}}, treatment = 'c6') => {
    const {headlinePhoto, headlinePhotoUrl, thumbnailUrl, images} = listing;
    const arrayOfImages = [headlinePhotoUrl, thumbnailUrl, headlinePhoto];

    if (images) {
        const image = images[0];
        const urlKey = Object.keys(image)[0];
        return image[urlKey];
    }

    return arrayOfImages.reduce((result, imgUrl) => {
        if (imgUrl) {
            if (imgUrl.uri) {
                return imgUrl.uri;
            }
            return imgUrl.replace('TREATMENT', treatment);
        }
        return result;
    }, '');
};

export const filterDestinationHits = (destinations) => {
    return destinations.filter((destination) => {
        const image = get(destination, 'imageHref');
        const breadcrumbsFirstLevelSimple = get(
            destination,
            'breadcrumbs[0].place.name.simple',
            null
        );
        const placeNameSimple = get(destination, 'place.name.simple', null);

        return image && (breadcrumbsFirstLevelSimple || placeNameSimple);
    });
};

export const filterPropertyHits = (properties, translations) => {
    return properties.filter((property) => {
        const image = getImageUrl(property);
        const listingId = get(property, 'listing.listingId');
        const accommodations =
            getAccommodations(property, translations) ||
            getLegacyAccommodations(property, translations);
        const averageRate = getAverageRate(
            property,
            translations.seoAveragePrice
        );

        return listingId && image && (accommodations || averageRate);
    });
};

export const fillHitLabels = (properties, translations) => {
    if (!properties || !properties.length) {
        return [];
    }
    const filteredProperties = filterPropertyHits(properties, translations);

    return filteredProperties.map((hit) => ({
        listingId: hit.listing.listingId,
        location: getLocation(hit),
        accommodations: getAccommodations(hit, translations),
        legacyAccommodations: getLegacyAccommodations(hit, translations),
        avgNightlyRate: getAverageRate(hit, translations.seoAveragePrice),
        totalRate: getTotalRate(hit, translations.seoTotalPrice),
        averagePriceSummary: getAveragePriceSummary(hit),
        instantBooking: get(hit, 'listing.instantBookable', false),
        averageRating: get(hit, 'listing.averageRating', 0),
        reviewCount: get(hit, 'listing.reviewCount', 0),
        imageUrl: getImageUrl(hit),
        propertyHref: get(hit, 'listing.detailPageUrl'),
        headline: get(hit, 'listing.propertyMetadata.headline'),
        spu: get(hit, 'listing.spu'),
        freeCancellationBadge: get(hit, 'listing.freeCancellationBadge'),
    }));
};
